/**
 * Evie's voices. One personality is drawn at random when a conversation starts and kept for the whole
 * session (see chatEngine `greet`), so the chat reads as one consistent voice while never repeating a
 * line word for word: every beat has at least two phrasings inside each personality.
 *
 * Only conversational beats live here. Policy wording (no-live-agent escalation, hand-off and reply
 * window, emergency, calming, triage, design-team hand-off, contact prompts) stays fixed in responses.ts.
 *
 * House rules for every line: welcoming and professional, written for dentists; first name at most
 * once per bubble; never promise a person joining the chat or a transfer; never "calm down".
 */

/** Picks one entry at random. `pick` is injectable so tests can make the choice deterministic. */
export const pickOne = <T>(items: readonly T[], pick: () => number = Math.random): T =>
  items[Math.floor(pick() * items.length) % items.length];

/** ", Jane" or "" so a missing name never leaves a dangling comma. */
export const withName = (firstName?: string) => (firstName ? `, ${firstName}` : '');
/** " Jane" or "". */
const sp = (firstName?: string) => (firstName ? ` ${firstName}` : '');

type Named = (firstName: string) => string;
type Heading = (firstName: string, count: number) => string;
type Ref = (refNo: string) => string;

export interface Personality {
  /** Stable id stored in the session context. */
  id: string;
  name: string;
  /** `name` is already defaulted to "there" when the user has no first name. */
  greetings: ReadonlyArray<(name: string) => string>;
  menuIntros: readonly string[];
  caseListHeadings: ReadonlyArray<Heading>;
  caseListHints: readonly string[];
  singleCaseIntros: ReadonlyArray<Named>;
  whichCase: readonly string[];
  whichCaseIsSlow: readonly string[];
  nothingForApproval: readonly string[];
  nothingInProgress: readonly string[];
  forApprovalIntros: readonly string[];
  inProgressIntros: readonly string[];
  noCases: readonly string[];
  casesError: readonly string[];
  /** Must name the case as `**refNo**`; the case picks are shown underneath. */
  caseNotFound: ReadonlyArray<Ref>;
  /** Leads into the menu buttons shown underneath. */
  fallbacks: readonly string[];
  thanks: ReadonlyArray<Named>;
  goodbyes: ReadonlyArray<Named>;
  /** Asks what went wrong and for the case number. */
  reportProblemPrompts: readonly string[];
  /** Asks, briefly, what support should help with. */
  askSummary: readonly string[];
  /** Leads into the menu buttons shown underneath. */
  contactCancelled: readonly string[];
}

/** Today's wording, kept word for word. Also the fallback for sessions saved before personalities existed. */
const classic: Personality = {
  id: 'classic',
  name: 'Classic',
  greetings: [
    (name) => `Hello ${name}! Evie here 😊 How can I help today?`,
    (name) => `Hi ${name}, I'm Evie. What can I do for you?`,
    (name) => `Hey ${name}, good to see you! Need a hand with a case?`,
    (name) => `Hi ${name}! I'm here if you want to check on any of your cases. What's on your mind?`,
    (name) => `Hey ${name}, Evie here. Ready whenever you are.`,
  ],
  menuIntros: [
    "Here's what I can do for you:",
    'A few things I can help with:',
    'Here are the things I can look into for you:',
  ],
  caseListHeadings: [
    (f, n) => `Hi${sp(f)}, here are your ${n} latest cases.`,
    (f, n) => `Hi${sp(f)}, your ${n} latest cases:`,
  ],
  caseListHints: [
    'Tap one to see its status, or type a case number.',
    'Tap one to see its status.',
  ],
  singleCaseIntros: [
    (f) => `Hi${sp(f)}, you have one open case.`,
    (f) => `Hi${sp(f)}, just the one open case.`,
  ],
  whichCase: [
    'Sure, which case? Just type the case number.',
    "Happy to look one up. What's the case number?",
  ],
  whichCaseIsSlow: [
    'Happy to look into it. Which case are you asking about?',
    'Let me check. Which case is this about?',
  ],
  nothingForApproval: [
    "You're all caught up, nothing needs your approval right now. 🎉",
    'Nothing is waiting on you at the moment. 🎉',
  ],
  nothingInProgress: [
    "Nothing's in the works right now. Once a case is submitted, I'll show it here.",
    "There's nothing in progress at the moment.",
  ],
  forApprovalIntros: [
    'These ones are waiting on you to approve:',
    'A few cases need your approval:',
  ],
  inProgressIntros: [
    "Here's what's being worked on at the moment:",
    'These cases are in progress right now:',
  ],
  noCases: [
    "Looks like there aren't any cases on file for your practice yet. As soon as you submit one, I'll be able to keep an eye on it for you.",
    "I can't see any cases for your practice yet. Once the first one is in, ask me again and I'll keep you posted.",
  ],
  casesError: [
    "Sorry, I'm having trouble reaching your case list right now. Give it a moment and try again?",
    "Hmm, I couldn't reach your case list just now. Please try again in a minute.",
  ],
  caseNotFound: [
    (r) => `I can't find case **${r}** on your account. Check the number, or pick one below.`,
    (r) => `Case **${r}** isn't on your account. Check the number, or pick one below.`,
  ],
  fallbacks: [
    "Hmm, I didn't quite catch that. Try one of these, or type a case number:",
    "I'm not sure I follow, sorry! Here's what I'm good at:",
    "That one's a bit outside what I can do. Here's what I can help with:",
  ],
  thanks: [
    (f) => `Any time${withName(f)}! Anything else I can look at?`,
    (f) => `Happy to help${withName(f)}. Shout if you need another case checked.`,
    (f) => `You're welcome${withName(f)}! I'm here if anything else comes up.`,
  ],
  goodbyes: [
    (f) => `Thanks for chatting${withName(f)}! I'll be here whenever you need a case checked. Bye for now 👋`,
    (f) => `No worries${withName(f)}, glad I could help. Come back any time. 👋`,
    (f) => `Take care${withName(f)}! Just open the chat again if anything else comes up.`,
  ],
  reportProblemPrompts: [
    "Sorry about that. Tell me what went wrong, and pop the case number in if it's about a specific case. I'll get it to the team.",
    "Oh no, sorry to hear it. Tell me what went wrong (include the case number if there is one) and I'll pass it along to the team.",
  ],
  askSummary: [
    'Of course. In a sentence or two, what would you like support to help with?',
    "Sure. Tell me briefly what you need help with and I'll pass it on.",
  ],
  contactCancelled: [
    "No problem, I've left that. Here's what else I can do:",
    "Okay, I've left that. Here's what else I can help with:",
  ],
};

const warm: Personality = {
  id: 'warm',
  name: 'Warm',
  greetings: [
    (name) => `Lovely to see you, ${name}. I'm Evie. What can I look into for you?`,
    (name) => `Hello ${name}, Evie here. Make yourself comfortable and tell me what you need.`,
    (name) => `Hi ${name}, it's so nice to have you back. What can I help with today?`,
  ],
  menuIntros: [
    "Here's what I'd love to help you with:",
    'A few things I can take care of for you:',
  ],
  caseListHeadings: [
    (f, n) => `Here you go${withName(f)}, your ${n} most recent cases.`,
    (f, n) => `Lovely${withName(f)}, I've gathered your ${n} latest cases for you.`,
  ],
  caseListHints: [
    'Tap any of them and I\'ll tell you where it\'s at.',
    'Pick one and I\'ll walk you through it, or type a case number.',
  ],
  singleCaseIntros: [
    (f) => `You've got just the one open case${withName(f)}, so here it is.`,
    (f) => `Only one case open at the moment${withName(f)}. Here's how it's going.`,
  ],
  whichCase: [
    'Of course. Which case would you like me to look at? Just pop the number in.',
    "I'd be glad to. What's the case number?",
  ],
  whichCaseIsSlow: [
    "I'm sorry it's dragging. Which case is it? I'll take a look.",
    "Let's find out together. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Good news, there's nothing waiting on your approval right now.",
    "You're all clear. Nothing needs your sign-off at the moment.",
  ],
  nothingInProgress: [
    "Nothing's in progress just now. The moment a case is submitted, I'll have it here for you.",
    "All quiet on the in-progress front at the moment.",
  ],
  forApprovalIntros: [
    'These are waiting for your approval whenever you have a moment:',
    "Here's what needs your sign-off:",
  ],
  inProgressIntros: [
    "Here's what the team is working on for you right now:",
    'These cases are moving along at the moment:',
  ],
  noCases: [
    "I can't see any cases for your practice yet. As soon as you submit your first one, I'll be right here to keep an eye on it.",
    "There aren't any cases on file just yet. Once one is in, come back and I'll keep you posted on it.",
  ],
  casesError: [
    "I'm so sorry, I can't reach your case list at the moment. Would you give it another go in a minute?",
    "Something's getting in the way of your case list right now. Please try again shortly, I'll be here.",
  ],
  caseNotFound: [
    (r) => `I couldn't find case **${r}** on your account, sorry. Double-check the number, or pick one from below.`,
    (r) => `Hmm, **${r}** doesn't seem to be one of yours. Have another look at the number, or choose one below.`,
  ],
  fallbacks: [
    "I'm sorry, I didn't quite follow that. Here's what I can help with:",
    "That one's a little beyond me, I'm afraid. These are the things I'm good at:",
  ],
  thanks: [
    (f) => `You're so welcome${withName(f)}. Is there anything else I can look at for you?`,
    (f) => `It's my pleasure${withName(f)}. I'm right here if anything else comes up.`,
  ],
  goodbyes: [
    (f) => `Take good care${withName(f)}. I'll be here whenever you need me. 👋`,
    (f) => `It was lovely chatting${withName(f)}. Come back any time, I'll be here.`,
  ],
  reportProblemPrompts: [
    "I'm sorry to hear that. Tell me what went wrong, with the case number if there is one, and I'll make sure the team hears about it.",
    "Oh, I'm sorry. Let me know what happened (and the case number if it's about a case) and I'll pass it straight on to the team.",
  ],
  askSummary: [
    "Of course. In a sentence or two, what would you like support to help you with?",
    'Happy to. Just tell me briefly what you need, and I\'ll pass it along.',
  ],
  contactCancelled: [
    "No trouble at all, I've set that aside. Here's what else I can do for you:",
    "That's fine, I've left it. Is there anything else I can help with?",
  ],
};

const upbeat: Personality = {
  id: 'upbeat',
  name: 'Upbeat',
  greetings: [
    (name) => `Hi ${name}! Evie here and ready to go. What are we checking today?`,
    (name) => `Hey ${name}! Great timing, I'm all set. What can I find for you?`,
    (name) => `Hello ${name}! Evie at the ready. Let's get you an answer!`,
  ],
  menuIntros: [
    "Here's what I can jump on right now:",
    'Pick one and off we go:',
  ],
  caseListHeadings: [
    (f, n) => `Here we go${withName(f)}! Your ${n} latest cases:`,
    (f, n) => `Fresh off the press${withName(f)}: your ${n} most recent cases.`,
  ],
  caseListHints: [
    'Tap one and I\'ll give you the full rundown!',
    'Pick a case or type a number and I\'m on it.',
  ],
  singleCaseIntros: [
    (f) => `Just one open case${withName(f)}, so this is the one!`,
    (f) => `One open case on the books${withName(f)}. Here's the latest!`,
  ],
  whichCase: [
    'Absolutely! Which case? Type the number and I\'ll pull it up.',
    "On it! What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let's get to the bottom of that! Which case is it?",
    'Happy to chase it. Which case are you waiting on?',
  ],
  nothingForApproval: [
    "Nothing waiting on you, you're all caught up! 🎉",
    'All clear! Nothing needs your approval right now. 🙌',
  ],
  nothingInProgress: [
    "Nothing in progress just now. Submit a case and I'll have it here in a flash!",
    'All quiet at the moment, nothing in progress.',
  ],
  forApprovalIntros: [
    'These are ready and waiting for your approval:',
    'Your sign-off is all these need:',
  ],
  inProgressIntros: [
    "Here's what's cooking right now:",
    'These are in full swing at the moment:',
  ],
  noCases: [
    "No cases on file for your practice yet! Submit your first one and I'll track it for you from day one.",
    "Nothing to show yet, but the moment your first case is in, I'll be keeping tabs on it.",
  ],
  casesError: [
    "Ah, I can't reach your case list right this second. Give it a moment and try again!",
    "Hit a snag reaching your cases, sorry! Try again in a minute and I'll be ready.",
  ],
  caseNotFound: [
    (r) => `Hmm, I can't find **${r}** on your account! Check the number, or grab one from below.`,
    (r) => `No luck with **${r}**, that one isn't on your account. Have another look, or pick one below.`,
  ],
  fallbacks: [
    "Oops, that one went over my head! Here's what I'm great at:",
    "Not sure I caught that, sorry! Try one of these or type a case number:",
  ],
  thanks: [
    (f) => `Any time${withName(f)}! What else can I find for you?`,
    (f) => `Glad I could help${withName(f)}! Anything else on the list?`,
  ],
  goodbyes: [
    (f) => `Thanks for stopping by${withName(f)}! Come back any time. 👋`,
    (f) => `Catch you later${withName(f)}! I'll be here when you need me. ✨`,
  ],
  reportProblemPrompts: [
    "Sorry about that! Tell me what went wrong, with the case number if there is one, and I'll get it to the team right away.",
    "Oh no! Let me know what happened (case number too, if it's about a case) and I'll pass it straight along.",
  ],
  askSummary: [
    "Sure thing! In a sentence or two, what should support help with?",
    "You got it. Tell me briefly what you need and I'll pass it on!",
  ],
  contactCancelled: [
    "No worries, I've dropped that! Here's what else I can do:",
    "All good, that's cancelled. What else can I help with?",
  ],
};

const calm: Personality = {
  id: 'calm',
  name: 'Calm',
  greetings: [
    (name) => `Hello ${name}. I'm Evie. Take your time, and tell me what you need.`,
    (name) => `Hi ${name}, Evie here. Whenever you're ready, let me know what I can look into.`,
    (name) => `Good to see you, ${name}. I'm here to help with your cases. Where would you like to start?`,
  ],
  menuIntros: [
    "Here's what I can help with:",
    'These are the things I can look into for you:',
  ],
  caseListHeadings: [
    (f, n) => `Here are your ${n} most recent cases${withName(f)}.`,
    (f, n) => `Your ${n} latest cases are below${withName(f)}.`,
  ],
  caseListHints: [
    'Choose one to see where it is, or type a case number.',
    'Select a case and I\'ll go through it with you.',
  ],
  singleCaseIntros: [
    (f) => `You have one open case at the moment${withName(f)}.`,
    (f) => `There's a single open case on your account${withName(f)}. Here it is.`,
  ],
  whichCase: [
    'Certainly. Which case would you like to see? Type the case number.',
    "Of course. What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let's take a look. Which case is this about?",
    "I'll check on that. Which case are you waiting on?",
  ],
  nothingForApproval: [
    'Nothing needs your approval at the moment.',
    "You're up to date. There's nothing waiting on your approval.",
  ],
  nothingInProgress: [
    'There are no cases in progress right now.',
    "Nothing is in progress at the moment. Once a case is submitted, you'll see it here.",
  ],
  forApprovalIntros: [
    'These cases are waiting on your approval:',
    "Here's what needs your approval:",
  ],
  inProgressIntros: [
    'These cases are in progress:',
    "Here's what is being worked on at the moment:",
  ],
  noCases: [
    "There are no cases on file for your practice yet. Once one is submitted, I'll be able to follow it for you.",
    "I can't see any cases for your practice at the moment. When the first one is in, ask me again and I'll keep you updated.",
  ],
  casesError: [
    "I'm not able to reach your case list right now. Please try again in a moment.",
    "Your case list isn't available just now. Give it a minute and try again.",
  ],
  caseNotFound: [
    (r) => `I can't find case **${r}** on your account. Check the number, or choose one below.`,
    (r) => `Case **${r}** isn't on your account. Have a look at the number, or pick one below.`,
  ],
  fallbacks: [
    "I'm not sure I understood that. Here's what I can help with:",
    "That's outside what I can do. These are the things I can look into:",
  ],
  thanks: [
    (f) => `You're welcome${withName(f)}. Is there anything else I can look at?`,
    (f) => `Glad to help${withName(f)}. Let me know if anything else comes up.`,
  ],
  goodbyes: [
    (f) => `Take care${withName(f)}. I'll be here whenever you need me.`,
    (f) => `Thanks for chatting${withName(f)}. Open the chat again any time.`,
  ],
  reportProblemPrompts: [
    "I'm sorry to hear that. Tell me what went wrong, with the case number if there is one, and I'll pass it to the team.",
    "Sorry about that. Describe what happened, including the case number if it's about a case, and I'll get it to the team.",
  ],
  askSummary: [
    'Of course. In a sentence or two, what would you like support to help with?',
    "Certainly. Tell me briefly what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "That's fine, I've left it. Here's what else I can do:",
    "No problem. I've set that aside. Here's what else I can help with:",
  ],
};

const brisk: Personality = {
  id: 'brisk',
  name: 'Brisk',
  greetings: [
    (name) => `Hi ${name}. Evie here. What do you need?`,
    (name) => `Hello ${name}. I'm Evie. Case number, or pick an option.`,
    (name) => `${name === 'there' ? 'Hi there' : `Hi ${name}`}. Evie here. Let's get you sorted.`,
  ],
  menuIntros: [
    'I can help with:',
    'Options:',
  ],
  caseListHeadings: [
    (f, n) => `Your ${n} latest cases${withName(f)}:`,
    (f, n) => `${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one for its status.',
    'Tap a case, or type a number.',
  ],
  singleCaseIntros: [
    (f) => `One open case${withName(f)}:`,
    (f) => `Just one case open${withName(f)}. Status below.`,
  ],
  whichCase: [
    'Which case? Type the number.',
    'Sure. Case number?',
  ],
  whichCaseIsSlow: [
    "I'll check. Which case?",
    'Which case is it? I\'ll have a look.',
  ],
  nothingForApproval: [
    'Nothing needs your approval.',
    'All approved. Nothing waiting on you.',
  ],
  nothingInProgress: [
    'Nothing in progress right now.',
    'No cases in progress at the moment.',
  ],
  forApprovalIntros: [
    'Waiting on your approval:',
    'Needs your sign-off:',
  ],
  inProgressIntros: [
    'In progress:',
    'Being worked on now:',
  ],
  noCases: [
    "No cases on file for your practice yet. Submit one and I'll track it.",
    "Nothing on file yet. Once a case is in, ask again and I'll keep you posted.",
  ],
  casesError: [
    "Can't reach your case list right now. Try again in a minute.",
    'Case list unavailable just now. Please retry shortly.',
  ],
  caseNotFound: [
    (r) => `**${r}** isn't on your account. Check the number, or pick one below.`,
    (r) => `No case **${r}** on your account. Try again, or choose one below.`,
  ],
  fallbacks: [
    "Didn't catch that. Pick one of these, or type a case number:",
    "Not something I can do. Here's what I can:",
  ],
  thanks: [
    (f) => `Welcome${withName(f)}. Anything else?`,
    (f) => `No problem${withName(f)}. What next?`,
  ],
  goodbyes: [
    (f) => `Thanks${withName(f)}. Back whenever you need me.`,
    (f) => `Done${withName(f)}. Open the chat any time.`,
  ],
  reportProblemPrompts: [
    "Sorry about that. What went wrong? Include the case number if there is one and I'll send it to the team.",
    "Noted. Tell me what happened, plus the case number if relevant, and I'll pass it on.",
  ],
  askSummary: [
    'Sure. In a sentence, what should support help with?',
    "Okay. Briefly, what do you need? I'll pass it on.",
  ],
  contactCancelled: [
    "Cancelled. Here's what else I can do:",
    'Left it. Other options:',
  ],
};

const professional: Personality = {
  id: 'professional',
  name: 'Professional',
  greetings: [
    (name) => `Good to see you, ${name}. I'm Evie, your case assistant. How can I help?`,
    (name) => `Hello ${name}, Evie here. I can check on your cases or point you to the right team. What do you need?`,
    (name) => `Welcome, ${name}. I'm Evie. Let me know what you'd like to look at today.`,
  ],
  menuIntros: [
    "Here's what I can assist with:",
    'I can help you with the following:',
  ],
  caseListHeadings: [
    (f, n) => `Here are your ${n} most recent cases${withName(f)}.`,
    (f, n) => `Your ${n} latest cases${withName(f)}:`,
  ],
  caseListHints: [
    'Select a case to see its current status, or enter a case number.',
    'Choose a case below for its details.',
  ],
  singleCaseIntros: [
    (f) => `You currently have one open case${withName(f)}.`,
    (f) => `There's one open case on your account${withName(f)}.`,
  ],
  whichCase: [
    'Certainly. Which case would you like me to check? Please enter the case number.',
    'Of course. What is the case number?',
  ],
  whichCaseIsSlow: [
    'I can look into that. Which case are you referring to?',
    "Let me check for you. Which case is this regarding?",
  ],
  nothingForApproval: [
    'Nothing is awaiting your approval at present.',
    "You're up to date. No cases need your approval right now.",
  ],
  nothingInProgress: [
    'There are no cases in progress at present.',
    "Nothing is currently in progress. Submitted cases will appear here.",
  ],
  forApprovalIntros: [
    'The following cases are awaiting your approval:',
    'These cases need your sign-off:',
  ],
  inProgressIntros: [
    'The following cases are currently in progress:',
    "Here's what is being worked on at present:",
  ],
  noCases: [
    "There are no cases on file for your practice yet. Once a case is submitted, I'll be able to track it for you.",
    "I can't find any cases for your practice at present. After your first submission, ask again and I'll keep you informed.",
  ],
  casesError: [
    "Apologies, I'm unable to reach your case list at the moment. Please try again shortly.",
    "I couldn't retrieve your case list just now. Please try again in a minute.",
  ],
  caseNotFound: [
    (r) => `I can't find case **${r}** on your account. Please check the number, or select one below.`,
    (r) => `Case **${r}** doesn't appear on your account. Verify the number, or choose one below.`,
  ],
  fallbacks: [
    "I'm not sure I understood that. Here's what I can assist with:",
    "That falls outside what I can do. These are the areas I can help with:",
  ],
  thanks: [
    (f) => `You're welcome${withName(f)}. Is there anything else I can assist with?`,
    (f) => `My pleasure${withName(f)}. Let me know if there's anything further.`,
  ],
  goodbyes: [
    (f) => `Thank you${withName(f)}. I'm here whenever you need to check on a case.`,
    (f) => `Goodbye for now${withName(f)}. Open the chat any time you need assistance.`,
  ],
  reportProblemPrompts: [
    "I'm sorry to hear that. Please describe what went wrong, including the case number if applicable, and I'll pass it to the team.",
    "Apologies for the trouble. Let me know what happened, with the case number if it concerns a case, and I'll forward it to the team.",
  ],
  askSummary: [
    'Certainly. In a sentence or two, what would you like support to assist with?',
    "Of course. Briefly describe what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "Understood, I've cancelled that. Here's what else I can assist with:",
    "No problem, that's been set aside. Other options:",
  ],
};

const cheerful: Personality = {
  id: 'cheerful',
  name: 'Cheerful',
  greetings: [
    (name) => `Hey ${name}, great to have you here! Evie at your service.`,
    (name) => `Hi ${name}! Evie here, happy to help. What are we looking at today?`,
    (name) => `Hello ${name}! Lovely day for checking on cases. What can I do for you?`,
  ],
  menuIntros: [
    "Here's the fun stuff I can do:",
    'Take your pick:',
  ],
  caseListHeadings: [
    (f, n) => `Here are your ${n} latest cases${withName(f)}! 😊`,
    (f, n) => `Ta-da${withName(f)}, your ${n} most recent cases:`,
  ],
  caseListHints: [
    'Tap one and I\'ll fill you in!',
    'Pick a case, or type a number, and I\'ll do the rest.',
  ],
  singleCaseIntros: [
    (f) => `Just one open case${withName(f)}, nice and simple!`,
    (f) => `One open case on the go${withName(f)}. Here's the latest.`,
  ],
  whichCase: [
    "Happy to! Which case? Just type the number.",
    "Sure thing! What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let's find out what's up! Which case is it?",
    "I'll take a look! Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Nothing waiting on you, hooray! 🎉",
    "You're all caught up, nothing needs approving. 🙌",
  ],
  nothingInProgress: [
    "Nothing in progress right now. Submit a case and I'll keep you posted!",
    'All quiet at the moment, nothing in the works.',
  ],
  forApprovalIntros: [
    'These are just waiting for your thumbs up:',
    'A few cases would love your approval:',
  ],
  inProgressIntros: [
    "Here's what's humming along right now:",
    'These cases are in progress as we speak:',
  ],
  noCases: [
    "No cases on file for your practice yet! Once you submit one, I'll be keeping an eye on it for you.",
    "Nothing to show yet, but as soon as your first case is in, I'll be tracking it with you.",
  ],
  casesError: [
    "Oh dear, I can't reach your case list right now. Give it a moment and try again?",
    "Hmm, your case list is playing hard to get. Try again in a minute!",
  ],
  caseNotFound: [
    (r) => `I couldn't spot **${r}** on your account! Check the number, or pick one below.`,
    (r) => `No sign of case **${r}** on your account. Have another look, or choose one below.`,
  ],
  fallbacks: [
    "Whoops, that one got past me! Here's what I'm good at:",
    "I didn't quite get that, sorry! Try one of these or type a case number:",
  ],
  thanks: [
    (f) => `Aw, any time${withName(f)}! Anything else I can look at?`,
    (f) => `Happy to help${withName(f)}! Give me a shout if you need another case checked.`,
  ],
  goodbyes: [
    (f) => `Bye for now${withName(f)}! Come back any time. 👋`,
    (f) => `Thanks for chatting${withName(f)}! Have a great day. 😊`,
  ],
  reportProblemPrompts: [
    "Oh no, sorry about that! Tell me what went wrong, with the case number if there is one, and I'll pass it to the team.",
    "Sorry to hear it! Let me know what happened (and the case number if it's about a case) and I'll get it to the team.",
  ],
  askSummary: [
    'Of course! In a sentence or two, what should support help with?',
    "Sure! Tell me briefly what you need and I'll pass it along.",
  ],
  contactCancelled: [
    "No worries, I've left that! Here's what else I can do:",
    "All good, that's dropped. What else can I help with?",
  ],
};

const easygoing: Personality = {
  id: 'easygoing',
  name: 'Easygoing',
  greetings: [
    (name) => `Hey ${name}, Evie here. No rush, what's up?`,
    (name) => `Hi ${name}. Evie here. Want me to check on a case, or something else?`,
    (name) => `Hey ${name}, good to see you. What can I look up for you?`,
  ],
  menuIntros: [
    "Here's what I can do:",
    'Whenever you\'re ready, pick one of these:',
  ],
  caseListHeadings: [
    (f, n) => `Here's where your ${n} latest cases are at${withName(f)}.`,
    (f, n) => `Your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one whenever you like, or type a case number.',
    'Pick one and I\'ll fill you in.',
  ],
  singleCaseIntros: [
    (f) => `Just the one open case${withName(f)}. Here's the latest.`,
    (f) => `You've only got one case open${withName(f)}, so here it is.`,
  ],
  whichCase: [
    'Sure. Which case? Type the number whenever you\'re ready.',
    "No problem. What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let's have a look. Which case is it?",
    'Fair enough. Which case are you waiting on?',
  ],
  nothingForApproval: [
    'Nothing waiting on you right now. All good.',
    "You're all caught up, nothing to approve.",
  ],
  nothingInProgress: [
    "Nothing in progress at the moment. Once a case is in, it'll show up here.",
    'All quiet, nothing in the works right now.',
  ],
  forApprovalIntros: [
    'These are waiting on your approval, whenever you get a sec:',
    'A few need your sign-off:',
  ],
  inProgressIntros: [
    "Here's what's being worked on:",
    'These are in progress at the moment:',
  ],
  noCases: [
    "No cases on file for your practice yet. Once you've submitted one, I'll keep an eye on it for you.",
    "Can't see any cases yet. Come back once the first one is in and I'll keep you posted.",
  ],
  casesError: [
    "Can't reach your case list right now, sorry. Give it a minute and try again?",
    "Your case list isn't loading just now. Try again in a bit.",
  ],
  caseNotFound: [
    (r) => `Can't see **${r}** on your account. Check the number, or pick one below.`,
    (r) => `Case **${r}** isn't one of yours as far as I can tell. Have another look, or choose one below.`,
  ],
  fallbacks: [
    "Not sure I follow, sorry. Here's what I can help with:",
    "That's a bit outside my lane. Try one of these, or type a case number:",
  ],
  thanks: [
    (f) => `No worries${withName(f)}. Anything else?`,
    (f) => `Any time${withName(f)}. I'm around if something else comes up.`,
  ],
  goodbyes: [
    (f) => `See you${withName(f)}. Open the chat again whenever. 👋`,
    (f) => `No worries${withName(f)}, catch you next time.`,
  ],
  reportProblemPrompts: [
    "Sorry about that. Tell me what went wrong, and the case number if there is one, and I'll pass it to the team.",
    "Ah, sorry to hear it. Let me know what happened (plus the case number if it's about a case) and I'll send it on.",
  ],
  askSummary: [
    'Sure. In a sentence or two, what should support help with?',
    "No problem. Tell me briefly what you need and I'll pass it along.",
  ],
  contactCancelled: [
    "No worries, I've left that. Here's what else I can do:",
    "All good, dropped it. Anything else?",
  ],
};

const coach: Personality = {
  id: 'coach',
  name: 'Coach',
  greetings: [
    (name) => `Hi ${name}! Evie here. Let's get your cases sorted. Where shall we start?`,
    (name) => `Hey ${name}, Evie here. Ready when you are. What's first on the list?`,
    (name) => `Hello ${name}! Let's make this quick and easy. What do you need?`,
  ],
  menuIntros: [
    "Here's what we can tackle:",
    'Pick a starting point:',
  ],
  caseListHeadings: [
    (f, n) => `Good start${withName(f)}. Here are your ${n} latest cases.`,
    (f, n) => `Let's begin with your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one and we\'ll dig in.',
    'Pick a case, or type a number, and let\'s go.',
  ],
  singleCaseIntros: [
    (f) => `One open case to focus on${withName(f)}. Here's where it stands.`,
    (f) => `Just one case open${withName(f)}, so let's look at it.`,
  ],
  whichCase: [
    "Let's do it. Which case? Type the number.",
    "Sure. What's the case number and we'll take it from there?",
  ],
  whichCaseIsSlow: [
    "Let's find out why. Which case is it?",
    "We'll get to the bottom of it. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Nothing waiting on you. You're ahead of the game! 🎉",
    "All approved, nothing on your plate right now.",
  ],
  nothingInProgress: [
    "Nothing in progress yet. Once a case is in, we'll track it here.",
    'Nothing in the works at the moment.',
  ],
  forApprovalIntros: [
    'These just need your approval to keep moving:',
    "Here's what's waiting on your sign-off:",
  ],
  inProgressIntros: [
    "Here's what's moving forward right now:",
    'These cases are in progress:',
  ],
  noCases: [
    "No cases on file for your practice yet. Submit your first one and we'll track it from the start.",
    "Nothing on file yet, but once the first case is in, ask me again and we'll keep it on track.",
  ],
  casesError: [
    "I can't reach your case list right now. Let's try again in a minute.",
    "Small hiccup reaching your cases. Give it a moment and we'll go again.",
  ],
  caseNotFound: [
    (r) => `I can't find **${r}** on your account. Check the number, or pick one below and we'll keep going.`,
    (r) => `Case **${r}** isn't on your account. Have another look, or choose one below.`,
  ],
  fallbacks: [
    "I didn't catch that one. Let's try one of these, or a case number:",
    "That's outside what I can do, but here's what we can work on:",
  ],
  thanks: [
    (f) => `Nice work${withName(f)}! What's next?`,
    (f) => `Any time${withName(f)}. Anything else we should look at?`,
  ],
  goodbyes: [
    (f) => `Great session${withName(f)}! Come back whenever you need a hand. 👋`,
    (f) => `That's a wrap${withName(f)}. I'll be here next time.`,
  ],
  reportProblemPrompts: [
    "Sorry about that. Tell me what went wrong, with the case number if there is one, and I'll get it to the team.",
    "Let's fix it. What happened? Include the case number if it's about a case and I'll pass it along.",
  ],
  askSummary: [
    "Of course. In a sentence or two, what should support help with?",
    "Let's do it. Tell me briefly what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "No problem, I've left that. Here's what else we can do:",
    "Okay, dropped. What's next?",
  ],
};

const concierge: Personality = {
  id: 'concierge',
  name: 'Concierge',
  greetings: [
    (name) => `Welcome back, ${name}. I'm Evie. How may I assist you today?`,
    (name) => `Good day, ${name}. Evie here, at your service. What can I look into for you?`,
    (name) => `Hello ${name}, and welcome. I'm Evie. Please let me know how I can help.`,
  ],
  menuIntros: [
    'I would be glad to help with any of the following:',
    'Please choose from the options below:',
  ],
  caseListHeadings: [
    (f, n) => `Here are your ${n} most recent cases${withName(f)}.`,
    (f, n) => `Your ${n} latest cases, at your convenience${withName(f)}:`,
  ],
  caseListHints: [
    'Please select a case to view its status, or enter a case number.',
    'Choose any case and I will provide the details.',
  ],
  singleCaseIntros: [
    (f) => `You have one open case at present${withName(f)}.`,
    (f) => `There is a single open case on your account${withName(f)}. Here are the details.`,
  ],
  whichCase: [
    'Certainly. Which case would you like? Please enter the case number.',
    'With pleasure. May I have the case number?',
  ],
  whichCaseIsSlow: [
    'I would be glad to look into that. Which case is it regarding?',
    'Allow me to check. Which case are you waiting on?',
  ],
  nothingForApproval: [
    'Nothing is awaiting your approval at present.',
    'You are fully up to date. No cases require your approval.',
  ],
  nothingInProgress: [
    'There are no cases in progress at the moment.',
    'Nothing is currently in progress. Submitted cases will appear here.',
  ],
  forApprovalIntros: [
    'The following cases await your approval:',
    'These cases require your sign-off:',
  ],
  inProgressIntros: [
    'The following cases are currently in progress:',
    'These cases are being attended to at present:',
  ],
  noCases: [
    'There are no cases on file for your practice yet. Once one is submitted, I will be glad to track it for you.',
    'I cannot see any cases for your practice at present. After your first submission, please ask again and I will keep you informed.',
  ],
  casesError: [
    'My apologies, I am unable to reach your case list at the moment. Please try again shortly.',
    'I could not retrieve your case list just now. Please allow a moment and try again.',
  ],
  caseNotFound: [
    (r) => `I am unable to find case **${r}** on your account. Please check the number, or select one below.`,
    (r) => `Case **${r}** does not appear on your account. Kindly verify the number, or choose one below.`,
  ],
  fallbacks: [
    'My apologies, I did not quite understand. Here is what I can assist with:',
    'That is outside what I am able to do. Please choose from the following:',
  ],
  thanks: [
    (f) => `You are most welcome${withName(f)}. Is there anything else I can assist with?`,
    (f) => `My pleasure${withName(f)}. Please let me know if there is anything further.`,
  ],
  goodbyes: [
    (f) => `Thank you${withName(f)}. I will be here whenever you need assistance.`,
    (f) => `Goodbye for now${withName(f)}. It was a pleasure to help.`,
  ],
  reportProblemPrompts: [
    'I am sorry to hear that. Please describe what went wrong, with the case number if applicable, and I will pass it to the team.',
    'My apologies for the trouble. Kindly let me know what happened, including the case number if relevant, and I will forward it to the team.',
  ],
  askSummary: [
    'Certainly. In a sentence or two, what would you like support to assist with?',
    'Of course. Please describe briefly what you need and I will pass it on.',
  ],
  contactCancelled: [
    'Understood, I have cancelled that. Here is what else I can assist with:',
    'Very well, that has been set aside. Please choose from the following:',
  ],
};

const plain: Personality = {
  id: 'plain',
  name: 'Plain-spoken',
  greetings: [
    (name) => `Hi ${name}, it's Evie. Tell me what you're after and I'll find it.`,
    (name) => `Hello ${name}. Evie here. I can check cases or get you to the right team. What do you need?`,
    (name) => `Hi ${name}. I'm Evie. Ask me about a case, or pick an option below.`,
  ],
  menuIntros: [
    'I can do these things:',
    "Here's what I can help with:",
  ],
  caseListHeadings: [
    (f, n) => `These are your ${n} latest cases${withName(f)}.`,
    (f, n) => `Your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one to see its status, or type a case number.',
    'Pick a case and I\'ll tell you where it is.',
  ],
  singleCaseIntros: [
    (f) => `You have one open case${withName(f)}. Here it is.`,
    (f) => `There's one open case on your account${withName(f)}.`,
  ],
  whichCase: [
    'Which case? Type the case number.',
    "Sure. What's the case number?",
  ],
  whichCaseIsSlow: [
    "I'll look into it. Which case is it?",
    'Tell me which case and I\'ll check.',
  ],
  nothingForApproval: [
    'Nothing needs your approval right now.',
    'There is nothing waiting on you.',
  ],
  nothingInProgress: [
    'Nothing is in progress right now.',
    "There are no cases in progress. Once one is submitted, it'll show here.",
  ],
  forApprovalIntros: [
    'These need your approval:',
    'Waiting on your approval:',
  ],
  inProgressIntros: [
    'These are in progress:',
    'Being worked on right now:',
  ],
  noCases: [
    "There are no cases on file for your practice yet. Submit one and I'll keep track of it.",
    "I can't see any cases for your practice. Once the first one is in, ask me again.",
  ],
  casesError: [
    "I can't reach your case list right now. Try again in a minute.",
    "Your case list isn't loading. Give it a moment and try again.",
  ],
  caseNotFound: [
    (r) => `Case **${r}** isn't on your account. Check the number, or pick one below.`,
    (r) => `I can't find **${r}** on your account. Try the number again, or choose one below.`,
  ],
  fallbacks: [
    "I didn't understand that. Pick one of these, or type a case number:",
    "I can't help with that one. Here's what I can do:",
  ],
  thanks: [
    (f) => `You're welcome${withName(f)}. Anything else?`,
    (f) => `No problem${withName(f)}. Let me know if you need anything else.`,
  ],
  goodbyes: [
    (f) => `Thanks${withName(f)}. Open the chat again whenever you need it.`,
    (f) => `Bye for now${withName(f)}. I'll be here when you need me.`,
  ],
  reportProblemPrompts: [
    "Sorry about that. Tell me what went wrong and the case number, if there is one. I'll send it to the team.",
    "Sorry to hear it. Describe what happened, with the case number if it's about a case, and I'll pass it on.",
  ],
  askSummary: [
    'Sure. In a sentence or two, what should support help with?',
    "Okay. Tell me briefly what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "Okay, I've left that. Here's what else I can do:",
    'Cancelled. Other things I can help with:',
  ],
};

const curious: Personality = {
  id: 'curious',
  name: 'Curious',
  greetings: [
    (name) => `Hi ${name}! Evie here. What's on your mind today, a case or something else?`,
    (name) => `Hello ${name}, Evie here. What brings you in? I'm keen to help.`,
    (name) => `Hey ${name}! I'm Evie. What shall we look into first?`,
  ],
  menuIntros: [
    "Here's what I can dig into:",
    'What would you like to explore?',
  ],
  caseListHeadings: [
    (f, n) => `Here are your ${n} latest cases${withName(f)}. Which one are you curious about?`,
    (f, n) => `I found your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one and I\'ll see what I can find out.',
    'Pick a case, or type a number, and I\'ll look closer.',
  ],
  singleCaseIntros: [
    (f) => `Interesting, just one open case${withName(f)}. Here's the latest on it.`,
    (f) => `You've got one case open${withName(f)}. Let's see how it's going.`,
  ],
  whichCase: [
    "Sure! Which case has caught your attention? Type the number.",
    "Happy to look. What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let's see what's holding it up. Which case?",
    "I'd like to know too. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Nothing waiting on you right now. All caught up! 🎉",
    "No approvals needed at the moment.",
  ],
  nothingInProgress: [
    "Nothing in progress yet. Once a case is in, I'll keep tabs on it here.",
    'Nothing in the works at the moment.',
  ],
  forApprovalIntros: [
    'These are waiting on your approval:',
    "Here's what needs a look from you:",
  ],
  inProgressIntros: [
    "Here's what's being worked on at the moment:",
    'These are in progress right now:',
  ],
  noCases: [
    "I can't see any cases for your practice yet. Once your first one is in, I'll be keeping an eye on it.",
    "No cases on file yet. Submit one and ask me again, I'd love to follow it with you.",
  ],
  casesError: [
    "Hmm, I can't reach your case list right now. Give it a minute and try again?",
    "Your case list isn't answering just now. Try again shortly.",
  ],
  caseNotFound: [
    (r) => `I couldn't find **${r}** on your account. Could the number be slightly off? Or pick one below.`,
    (r) => `Case **${r}** isn't on your account. Have another look at the number, or choose one below.`,
  ],
  fallbacks: [
    "I'm not sure what you meant there. Was it one of these, or a case number?",
    "That's a new one on me! Here's what I can help with:",
  ],
  thanks: [
    (f) => `Any time${withName(f)}! Anything else you're wondering about?`,
    (f) => `Glad to help${withName(f)}. What else can I find out for you?`,
  ],
  goodbyes: [
    (f) => `Thanks for chatting${withName(f)}! Come back whenever you're curious about a case. 👋`,
    (f) => `Bye for now${withName(f)}. I'll be here next time.`,
  ],
  reportProblemPrompts: [
    "Sorry about that. What went wrong? Include the case number if there is one and I'll pass it to the team.",
    "I'd like to understand what happened. Tell me, with the case number if it's about a case, and I'll send it to the team.",
  ],
  askSummary: [
    'Of course. In a sentence or two, what should support help with?',
    "Sure. What do you need, briefly? I'll pass it on.",
  ],
  contactCancelled: [
    "No problem, I've left that. What else can I look into?",
    "Okay, dropped. Here's what else I can do:",
  ],
};

const reassuring: Personality = {
  id: 'reassuring',
  name: 'Reassuring',
  greetings: [
    (name) => `Hello ${name}, Evie here. Whatever it is, we'll get to the bottom of it.`,
    (name) => `Hi ${name}. I'm Evie, and you're in good hands. What can I help with?`,
    (name) => `Good to see you, ${name}. Evie here. Tell me what you need and I'll take it from there.`,
  ],
  menuIntros: [
    "Here's what I can take care of:",
    'Any of these, and I\'ll handle it:',
  ],
  caseListHeadings: [
    (f, n) => `Here are your ${n} latest cases${withName(f)}. Everything's in one place.`,
    (f, n) => `Your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one and I\'ll tell you exactly where it is.',
    'Pick a case, or type a number, and I\'ll take a proper look.',
  ],
  singleCaseIntros: [
    (f) => `Just one open case${withName(f)}, and I've got it right here.`,
    (f) => `You have one open case${withName(f)}. Here's where it stands.`,
  ],
  whichCase: [
    "Of course. Which case? Type the number and I'll pull it up.",
    "No problem at all. What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let's see what's going on. Which case is it?",
    "I'll find out for you. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Good news, nothing needs your approval right now.",
    "You're all caught up. Nothing is waiting on you.",
  ],
  nothingInProgress: [
    "Nothing in progress at the moment. As soon as a case is in, I'll have it here.",
    'Nothing is in the works right now.',
  ],
  forApprovalIntros: [
    'These are waiting on your approval, no rush:',
    "Here's what needs your sign-off:",
  ],
  inProgressIntros: [
    "Here's what's being looked after right now:",
    'These cases are in progress:',
  ],
  noCases: [
    "No cases on file for your practice yet. Once you submit one, I'll keep a close eye on it for you.",
    "I can't see any cases yet. When the first one is in, ask me again and I'll keep you updated.",
  ],
  casesError: [
    "I can't reach your case list right now, but it's nothing to worry about. Try again in a minute.",
    "Your case list isn't loading just now. Give it a moment and it should come right.",
  ],
  caseNotFound: [
    (r) => `I can't find **${r}** on your account. Check the number, or pick one below and we'll sort it.`,
    (r) => `Case **${r}** isn't on your account. Have another look at the number, or choose one below.`,
  ],
  fallbacks: [
    "I didn't quite catch that, but no problem. Here's what I can help with:",
    "That one's outside what I can do, but these are all easy:",
  ],
  thanks: [
    (f) => `You're welcome${withName(f)}. I'm here if anything else comes up.`,
    (f) => `Any time${withName(f)}. Is there anything else I can look at?`,
  ],
  goodbyes: [
    (f) => `Take care${withName(f)}. I'm here whenever you need me. 👋`,
    (f) => `Thanks for chatting${withName(f)}. Open the chat again any time.`,
  ],
  reportProblemPrompts: [
    "I'm sorry that happened. Tell me what went wrong, with the case number if there is one, and I'll make sure the team sees it.",
    "Sorry about that, we'll get it looked at. What happened? Include the case number if it's about a case.",
  ],
  askSummary: [
    'Of course. In a sentence or two, what would you like support to help with?',
    "Sure. Tell me briefly what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "No problem, I've left that. Here's what else I can do:",
    "That's fine, it's cancelled. Anything else I can help with?",
  ],
};

const playful: Personality = {
  id: 'playful',
  name: 'Playful',
  greetings: [
    (name) => `Hi ${name}! Evie reporting for duty 🫡 What can I dig up for you?`,
    (name) => `Hey ${name}, Evie here. Case detective at your service. What's the mystery?`,
    (name) => `Hello ${name}! Evie here, kettle's on. What are we looking at?`,
  ],
  menuIntros: [
    "Here's my bag of tricks:",
    'Pick your adventure:',
  ],
  caseListHeadings: [
    (f, n) => `Drumroll${withName(f)}... your ${n} latest cases:`,
    (f, n) => `Here are your ${n} most recent cases${withName(f)}, hot off the press.`,
  ],
  caseListHints: [
    'Tap one and I\'ll spill the details.',
    'Pick a case, or type a number, and I\'ll do some digging.',
  ],
  singleCaseIntros: [
    (f) => `Just one open case${withName(f)}, so this one gets the spotlight.`,
    (f) => `One lonely open case${withName(f)}. Here's its story so far.`,
  ],
  whichCase: [
    "Sure thing! Which case? Give me the number and I'm on the case. 🕵️",
    "Say the word. What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let's see what's dawdling. Which case is it?",
    "Time to investigate. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Nothing waiting on you. Put your feet up! 🎉",
    "All approved, nothing on your plate. Nice one.",
  ],
  nothingInProgress: [
    "Nothing in progress right now. Submit a case and I'll keep it on my radar.",
    "All quiet in the workshop at the moment.",
  ],
  forApprovalIntros: [
    'These just need your royal seal of approval:',
    "Here's what's waiting on your thumbs up:",
  ],
  inProgressIntros: [
    "Here's what's cooking at the moment:",
    'These cases are busy being worked on:',
  ],
  noCases: [
    "No cases on file for your practice yet! Submit your first one and I'll keep watch like a hawk.",
    "Nothing on file yet, but the moment your first case is in, I'll be tracking it.",
  ],
  casesError: [
    "Rats, I can't reach your case list right now. Give it a minute and try again?",
    "Your case list is being shy. Try again shortly!",
  ],
  caseNotFound: [
    (r) => `No sign of **${r}** on your account! Check the number, or pick one from below.`,
    (r) => `I searched high and low, but **${r}** isn't on your account. Have another look, or choose one below.`,
  ],
  fallbacks: [
    "That one flew right over my head! Here's what I'm actually good at:",
    "Hmm, you've stumped me. Try one of these, or type a case number:",
  ],
  thanks: [
    (f) => `Any time${withName(f)}! What else can I dig up?`,
    (f) => `Happy to help${withName(f)}. Shout if there's another case to chase.`,
  ],
  goodbyes: [
    (f) => `Catch you later${withName(f)}! I'll be right here when you need me. 👋`,
    (f) => `Thanks for chatting${withName(f)}. Mission accomplished! Back any time.`,
  ],
  reportProblemPrompts: [
    "Sorry about that! Tell me what went wrong, with the case number if there is one, and I'll get it to the team.",
    "Oh no, not ideal. What happened? Pop the case number in if it's about a case and I'll pass it on.",
  ],
  askSummary: [
    'Sure thing! In a sentence or two, what should support help with?',
    "You got it. Tell me briefly what you need and I'll pass it along.",
  ],
  contactCancelled: [
    "No worries, I've dropped that. Here's what else I can do:",
    "Scrapped! What else can I help with?",
  ],
};

const thoughtful: Personality = {
  id: 'thoughtful',
  name: 'Thoughtful',
  greetings: [
    (name) => `Hello ${name}. I'm Evie. Let me know what you'd like to look at and I'll take it from there.`,
    (name) => `Hi ${name}, Evie here. I'm happy to check on a case or point you to the right people. What would help most?`,
    (name) => `Good to see you, ${name}. Evie here. What can I look into for you today?`,
  ],
  menuIntros: [
    "Here's what I'm able to help with:",
    'These are the things I can look into:',
  ],
  caseListHeadings: [
    (f, n) => `Here are your ${n} most recent cases${withName(f)}.`,
    (f, n) => `I've pulled together your ${n} latest cases${withName(f)}:`,
  ],
  caseListHints: [
    'Choose one and I\'ll explain where it is, or type a case number.',
    'Select a case and I\'ll go through it with you.',
  ],
  singleCaseIntros: [
    (f) => `You have one open case at the moment${withName(f)}. Here's how it's going.`,
    (f) => `There's a single open case on your account${withName(f)}.`,
  ],
  whichCase: [
    'Certainly. Which case would you like me to look at? Type the number.',
    "Of course. What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let me look into that properly. Which case is it?",
    "I'll check what's happening. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Nothing needs your approval at the moment, so you're up to date.",
    'There is nothing waiting on your approval right now.',
  ],
  nothingInProgress: [
    "Nothing is in progress at the moment. Once a case is submitted, I'll show it here.",
    'There are no cases in progress just now.',
  ],
  forApprovalIntros: [
    'These cases are waiting on your approval:',
    "Here's what needs your consideration:",
  ],
  inProgressIntros: [
    "Here's what is being worked on at the moment:",
    'These cases are currently in progress:',
  ],
  noCases: [
    "There aren't any cases on file for your practice yet. Once one is submitted, I'll be able to follow it for you.",
    "I can't see any cases for your practice at the moment. When the first one is in, ask again and I'll keep you informed.",
  ],
  casesError: [
    "I'm sorry, I can't reach your case list at the moment. Please try again in a minute.",
    "Your case list isn't available right now. Give it a moment and try again.",
  ],
  caseNotFound: [
    (r) => `I couldn't find case **${r}** on your account. It may be worth checking the number, or pick one below.`,
    (r) => `Case **${r}** doesn't appear on your account. Have a look at the number, or choose one below.`,
  ],
  fallbacks: [
    "I'm not certain I understood that. Here's what I can help with:",
    "That's outside what I'm able to do. These are the things I can look into:",
  ],
  thanks: [
    (f) => `You're very welcome${withName(f)}. Is there anything else I can look at?`,
    (f) => `Glad I could help${withName(f)}. Let me know if anything else comes up.`,
  ],
  goodbyes: [
    (f) => `Thank you${withName(f)}. I'll be here whenever you need to check on a case.`,
    (f) => `Take care${withName(f)}. Open the chat again any time.`,
  ],
  reportProblemPrompts: [
    "I'm sorry to hear that. Please tell me what went wrong, with the case number if there is one, and I'll pass it to the team.",
    "Sorry about that. Describe what happened, including the case number if it's about a case, and I'll make sure the team gets it.",
  ],
  askSummary: [
    'Of course. In a sentence or two, what would you like support to help with?',
    "Certainly. Tell me briefly what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "That's fine, I've left it. Here's what else I can do:",
    "Understood. I've set that aside. Here's what else I can help with:",
  ],
};

const cando: Personality = {
  id: 'cando',
  name: 'Can-do',
  greetings: [
    (name) => `Hi ${name}, Evie here. Point me at a case and I'll get you an answer.`,
    (name) => `Hey ${name}! Evie here. Whatever you need checked, consider it done.`,
    (name) => `Hello ${name}. Evie here, ready to help. What are we sorting today?`,
  ],
  menuIntros: [
    "Here's what I can get done for you:",
    'Say the word and I\'ll handle it:',
  ],
  caseListHeadings: [
    (f, n) => `Done${withName(f)}. Here are your ${n} latest cases.`,
    (f, n) => `Here's your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one and I\'ll get you the details.',
    'Pick a case, or type a number, and I\'ll sort it.',
  ],
  singleCaseIntros: [
    (f) => `One open case${withName(f)}. Here's where it's at.`,
    (f) => `Just the one case open${withName(f)}, so here's the latest.`,
  ],
  whichCase: [
    "Can do. Which case? Type the number.",
    "On it. What's the case number?",
  ],
  whichCaseIsSlow: [
    "I'll find out. Which case is it?",
    "Let me chase that up. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Nothing waiting on you. All done! 🎉",
    'Nothing needs your approval right now.',
  ],
  nothingInProgress: [
    "Nothing in progress right now. Once a case is in, I'll have it here.",
    'Nothing in the works at the moment.',
  ],
  forApprovalIntros: [
    'These are ready for your approval:',
    "Here's what needs your sign-off:",
  ],
  inProgressIntros: [
    "Here's what's being worked on:",
    'These are in progress:',
  ],
  noCases: [
    "No cases on file for your practice yet. Submit one and I'll keep track of it for you.",
    "Nothing on file yet. Once the first case is in, ask me again and I'll keep you posted.",
  ],
  casesError: [
    "I can't reach your case list right now. Give it a minute and I'll have another go.",
    "Your case list isn't loading just now. Try again shortly.",
  ],
  caseNotFound: [
    (r) => `I can't find **${r}** on your account. Check the number, or pick one below and I'll get on it.`,
    (r) => `Case **${r}** isn't on your account. Have another look, or choose one below.`,
  ],
  fallbacks: [
    "I didn't catch that. Try one of these, or type a case number:",
    "Not something I can do, but these are:",
  ],
  thanks: [
    (f) => `No problem${withName(f)}! What else can I do?`,
    (f) => `Any time${withName(f)}. Need anything else sorted?`,
  ],
  goodbyes: [
    (f) => `All done${withName(f)}! Back whenever you need me. 👋`,
    (f) => `Thanks${withName(f)}. Open the chat again any time.`,
  ],
  reportProblemPrompts: [
    "Sorry about that. Tell me what went wrong, with the case number if there is one, and I'll get it to the team.",
    "Let's get that fixed. What happened? Include the case number if it's about a case and I'll pass it on.",
  ],
  askSummary: [
    'Sure. In a sentence or two, what should support help with?',
    "Can do. Tell me briefly what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "Done, I've left that. Here's what else I can do:",
    "Cancelled. What else can I sort for you?",
  ],
};

const gentle: Personality = {
  id: 'gentle',
  name: 'Gentle',
  greetings: [
    (name) => `Hi ${name}, it's Evie. I'm here whenever you're ready.`,
    (name) => `Hello ${name}. Evie here. No hurry at all, just let me know what you need.`,
    (name) => `Hi ${name}, Evie here. Would you like me to check on a case for you?`,
  ],
  menuIntros: [
    "Here's what I can help with, whenever you like:",
    'A few things I can do for you:',
  ],
  caseListHeadings: [
    (f, n) => `Here are your ${n} latest cases${withName(f)}.`,
    (f, n) => `Your ${n} most recent cases${withName(f)}, whenever you'd like to look:`,
  ],
  caseListHints: [
    'Tap one whenever you\'re ready, or type a case number.',
    'Choose a case and I\'ll gently walk you through it.',
  ],
  singleCaseIntros: [
    (f) => `You have one open case${withName(f)}. Here's how it's going.`,
    (f) => `Just the one open case${withName(f)}. Here it is.`,
  ],
  whichCase: [
    "Of course. Which case would you like? Type the number whenever you're ready.",
    "Happy to. What's the case number?",
  ],
  whichCaseIsSlow: [
    "I'm sorry it's taking a while. Which case is it? I'll take a look.",
    "Let's see what's happening. Which case are you waiting on?",
  ],
  nothingForApproval: [
    'Nothing needs your approval right now.',
    "You're up to date. Nothing is waiting on you.",
  ],
  nothingInProgress: [
    "Nothing is in progress at the moment. Once a case is in, I'll have it here for you.",
    'There is nothing in the works right now.',
  ],
  forApprovalIntros: [
    'These are waiting on your approval, whenever you have a moment:',
    "Here's what needs your sign-off:",
  ],
  inProgressIntros: [
    "Here's what's being worked on at the moment:",
    'These cases are in progress:',
  ],
  noCases: [
    "There aren't any cases on file for your practice yet. Once you submit one, I'll keep an eye on it for you.",
    "I can't see any cases yet. When the first one is in, ask me again and I'll keep you posted.",
  ],
  casesError: [
    "I'm sorry, I can't reach your case list right now. Would you try again in a minute?",
    "Your case list isn't loading just now. Give it a little while and try again.",
  ],
  caseNotFound: [
    (r) => `I couldn't find case **${r}** on your account. Perhaps check the number, or pick one below.`,
    (r) => `Case **${r}** isn't on your account. Have another look at the number, or choose one below.`,
  ],
  fallbacks: [
    "I'm sorry, I didn't quite follow. Here's what I can help with:",
    "That's a little outside what I can do. These are the things I can look into:",
  ],
  thanks: [
    (f) => `You're welcome${withName(f)}. Is there anything else I can help with?`,
    (f) => `I'm glad I could help${withName(f)}. I'm here if anything else comes up.`,
  ],
  goodbyes: [
    (f) => `Take care${withName(f)}. I'll be here whenever you need me.`,
    (f) => `Bye for now${withName(f)}. Open the chat again any time. 👋`,
  ],
  reportProblemPrompts: [
    "I'm sorry that happened. Tell me what went wrong, with the case number if there is one, and I'll pass it to the team.",
    "Sorry to hear it. Whenever you're ready, let me know what happened (and the case number if it's about a case) and I'll send it on.",
  ],
  askSummary: [
    'Of course. In a sentence or two, what would you like support to help with?',
    "Sure. Tell me briefly what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "That's fine, I've left it. Here's what else I can do:",
    "No problem at all. Here's what else I can help with:",
  ],
};

const crisp: Personality = {
  id: 'crisp',
  name: 'Crisp',
  greetings: [
    (name) => `${name === 'there' ? 'Hi there' : `${name}, hi`}. Evie here. Case number, or pick an option below.`,
    (name) => `Hi ${name}. Evie. What can I check?`,
    (name) => `Hello ${name}. Evie here. Ready.`,
  ],
  menuIntros: [
    'I can:',
    'Choose one:',
  ],
  caseListHeadings: [
    (f, n) => `Latest ${n} cases${withName(f)}:`,
    (f, n) => `Your ${n} newest cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap for status.',
    'Tap one, or type a case number.',
  ],
  singleCaseIntros: [
    (f) => `One open case${withName(f)}.`,
    (f) => `Single open case${withName(f)}. Here it is.`,
  ],
  whichCase: [
    'Case number?',
    'Which case? Type the number.',
  ],
  whichCaseIsSlow: [
    'Which case? I\'ll check.',
    'Case number and I\'ll look.',
  ],
  nothingForApproval: [
    'Nothing to approve.',
    'No approvals waiting.',
  ],
  nothingInProgress: [
    'Nothing in progress.',
    'No cases in progress right now.',
  ],
  forApprovalIntros: [
    'For approval:',
    'Awaiting your approval:',
  ],
  inProgressIntros: [
    'In progress:',
    'Currently in progress:',
  ],
  noCases: [
    'No cases on file yet. Submit one and I\'ll track it.',
    'Nothing on file for your practice yet. Ask again once a case is in.',
  ],
  casesError: [
    "Can't reach your case list. Try again in a minute.",
    'Case list unavailable. Retry shortly.',
  ],
  caseNotFound: [
    (r) => `**${r}** not found on your account. Check the number, or pick one below.`,
    (r) => `No case **${r}** here. Try again, or choose one below.`,
  ],
  fallbacks: [
    "Didn't catch that. Options:",
    "Can't do that one. I can:",
  ],
  thanks: [
    (f) => `Welcome${withName(f)}. Anything else?`,
    (f) => `Any time${withName(f)}. Next?`,
  ],
  goodbyes: [
    (f) => `Bye${withName(f)}. Here when you need me.`,
    (f) => `Thanks${withName(f)}. Back any time.`,
  ],
  reportProblemPrompts: [
    "Sorry. What went wrong? Add the case number if there is one and I'll send it to the team.",
    "Noted. Describe the problem, with the case number if relevant. I'll pass it on.",
  ],
  askSummary: [
    'One sentence: what should support help with?',
    "Briefly, what do you need? I'll pass it on.",
  ],
  contactCancelled: [
    'Cancelled. Options:',
    "Left it. Here's what else I can do:",
  ],
};

const host: Personality = {
  id: 'host',
  name: 'Host',
  greetings: [
    (name) => `Come on in, ${name}! Evie here. What can I get you up to speed on?`,
    (name) => `Welcome, ${name}! I'm Evie. Pull up a chair and tell me what you need.`,
    (name) => `Hello ${name}, so glad you dropped by. Evie here. What can I help with?`,
  ],
  menuIntros: [
    "Here's what's on the menu:",
    'Help yourself to any of these:',
  ],
  caseListHeadings: [
    (f, n) => `Here you are${withName(f)}, your ${n} latest cases.`,
    (f, n) => `I've laid out your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one and I\'ll fill you in on where it is.',
    'Pick any case, or type a number, and I\'ll take care of the rest.',
  ],
  singleCaseIntros: [
    (f) => `Just the one open case${withName(f)}. Here's how it's getting on.`,
    (f) => `One open case on your account${withName(f)}, and here it is.`,
  ],
  whichCase: [
    "Of course! Which case? Type the number and I'll bring it over.",
    "Gladly. What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let's see what's keeping it. Which case is it?",
    "I'll check on that for you. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Nothing waiting on you, you're all caught up! 🎉",
    'Nothing needs your approval right now.',
  ],
  nothingInProgress: [
    "Nothing in progress at the moment. Once a case is in, I'll have it ready here.",
    'All quiet, nothing in the works right now.',
  ],
  forApprovalIntros: [
    'These are waiting on your approval:',
    "Here's what needs your sign-off:",
  ],
  inProgressIntros: [
    "Here's what's being worked on right now:",
    'These cases are in progress:',
  ],
  noCases: [
    "No cases on file for your practice yet. Once you submit one, I'll be keeping an eye on it for you.",
    "I can't see any cases yet. When the first one is in, ask me again and I'll keep you posted.",
  ],
  casesError: [
    "Sorry, I can't reach your case list right now. Give it a moment and try again?",
    "Your case list isn't loading just now. Try again in a minute, I'll be here.",
  ],
  caseNotFound: [
    (r) => `I can't find **${r}** on your account, sorry. Check the number, or pick one from below.`,
    (r) => `Case **${r}** isn't on your account. Have another look, or choose one below.`,
  ],
  fallbacks: [
    "Sorry, I didn't quite catch that. Here's what I can help with:",
    "That one's not something I can do, but these are:",
  ],
  thanks: [
    (f) => `You're very welcome${withName(f)}! Anything else I can get you?`,
    (f) => `My pleasure${withName(f)}. I'm here if anything else comes up.`,
  ],
  goodbyes: [
    (f) => `Thanks for dropping by${withName(f)}! Come back any time. 👋`,
    (f) => `Take care${withName(f)}. The door's always open.`,
  ],
  reportProblemPrompts: [
    "I'm sorry about that. Tell me what went wrong, with the case number if there is one, and I'll pass it to the team.",
    "Sorry to hear it. Let me know what happened (and the case number if it's about a case) and I'll get it to the team.",
  ],
  askSummary: [
    'Of course. In a sentence or two, what would you like support to help with?',
    "Happy to. Tell me briefly what you need and I'll pass it along.",
  ],
  contactCancelled: [
    "No problem, I've left that. Here's what else is on offer:",
    "That's fine, cancelled. What else can I help with?",
  ],
};

const collaborative: Personality = {
  id: 'collaborative',
  name: 'Collaborative',
  greetings: [
    (name) => `Hi ${name}! Evie here. Let's see where your cases are up to.`,
    (name) => `Hey ${name}, Evie here. What shall we look at together?`,
    (name) => `Hello ${name}! I'm Evie. Let's get you what you need.`,
  ],
  menuIntros: [
    "Here's what we can do:",
    "Let's start with one of these:",
  ],
  caseListHeadings: [
    (f, n) => `Here are your ${n} latest cases${withName(f)}. Let's take a look.`,
    (f, n) => `We've got your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one and we\'ll go through it together.',
    'Pick a case, or type a number, and we\'ll take it from there.',
  ],
  singleCaseIntros: [
    (f) => `We've just got one open case to look at${withName(f)}.`,
    (f) => `One open case${withName(f)}. Let's see how it's going.`,
  ],
  whichCase: [
    "Sure. Which case shall we look at? Type the number.",
    "Let's do it. What's the case number?",
  ],
  whichCaseIsSlow: [
    "Let's work out what's holding it up. Which case?",
    "We'll get to the bottom of it. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Nothing waiting on you. We're all caught up! 🎉",
    "Nothing needs your approval right now.",
  ],
  nothingInProgress: [
    "Nothing in progress at the moment. Once a case is in, we'll track it here.",
    "There's nothing in the works right now.",
  ],
  forApprovalIntros: [
    'These are waiting on your approval:',
    "Here's what we need your sign-off on:",
  ],
  inProgressIntros: [
    "Here's what's being worked on:",
    'These cases are in progress:',
  ],
  noCases: [
    "No cases on file for your practice yet. Submit one and we'll keep track of it together.",
    "I can't see any cases yet. Once the first one is in, ask me again and we'll follow it.",
  ],
  casesError: [
    "I can't reach your case list right now. Let's try again in a minute.",
    "Your case list isn't loading just now. Give it a moment and we'll try again.",
  ],
  caseNotFound: [
    (r) => `I can't find **${r}** on your account. Let's check the number, or pick one below.`,
    (r) => `Case **${r}** isn't on your account. Have another look, or choose one below and we'll carry on.`,
  ],
  fallbacks: [
    "I didn't quite catch that. Let's try one of these, or a case number:",
    "That's outside what I can do, but here's what we can:",
  ],
  thanks: [
    (f) => `Any time${withName(f)}! What else shall we look at?`,
    (f) => `Glad we got there${withName(f)}. Anything else?`,
  ],
  goodbyes: [
    (f) => `Thanks for chatting${withName(f)}! We'll pick it up again whenever you need. 👋`,
    (f) => `Nice working with you${withName(f)}. Back any time.`,
  ],
  reportProblemPrompts: [
    "Sorry about that. Tell me what went wrong, with the case number if there is one, and we'll get it to the team.",
    "Let's sort it. What happened? Include the case number if it's about a case and I'll pass it on.",
  ],
  askSummary: [
    'Of course. In a sentence or two, what should support help with?',
    "Sure. Tell me briefly what you need and we'll pass it on.",
  ],
  contactCancelled: [
    "No problem, I've left that. Here's what else we can do:",
    "Okay, dropped. What's next?",
  ],
};

const appreciative: Personality = {
  id: 'appreciative',
  name: 'Appreciative',
  greetings: [
    (name) => `Hi ${name}, thanks for dropping in. I'm Evie. What can I help with?`,
    (name) => `Hello ${name}! Evie here. Thanks for stopping by, what can I look into for you?`,
    (name) => `Hi ${name}, good to have you here. I'm Evie. How can I help today?`,
  ],
  menuIntros: [
    "Here's what I can help with:",
    'Thanks for asking. I can do any of these:',
  ],
  caseListHeadings: [
    (f, n) => `Thanks for waiting${withName(f)}. Here are your ${n} latest cases.`,
    (f, n) => `Here are your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one to see its status, or type a case number.',
    'Pick a case and I\'ll fill you in.',
  ],
  singleCaseIntros: [
    (f) => `You have one open case${withName(f)}. Here's the latest.`,
    (f) => `Just the one open case${withName(f)}, and here it is.`,
  ],
  whichCase: [
    "Of course. Which case? Type the number and I'll find it.",
    "Thanks. What's the case number?",
  ],
  whichCaseIsSlow: [
    "Thanks for flagging it. Which case is it?",
    "I appreciate your patience. Which case are you waiting on?",
  ],
  nothingForApproval: [
    "Nothing waiting on you. Thanks for keeping on top of things! 🎉",
    'Nothing needs your approval right now.',
  ],
  nothingInProgress: [
    "Nothing in progress at the moment. Once a case is in, I'll have it here.",
    'Nothing in the works right now.',
  ],
  forApprovalIntros: [
    'These are waiting on your approval:',
    "Here's what needs your sign-off, thanks in advance:",
  ],
  inProgressIntros: [
    "Here's what's being worked on:",
    'These cases are in progress:',
  ],
  noCases: [
    "No cases on file for your practice yet. Once you submit one, I'll be happy to keep an eye on it.",
    "I can't see any cases yet. When the first one is in, ask me again and I'll keep you posted.",
  ],
  casesError: [
    "Sorry, I can't reach your case list right now. Thanks for your patience, try again in a minute?",
    "Your case list isn't loading just now. Please try again shortly.",
  ],
  caseNotFound: [
    (r) => `I can't find **${r}** on your account. Thanks for checking the number, or pick one below.`,
    (r) => `Case **${r}** isn't on your account. Have another look, or choose one below.`,
  ],
  fallbacks: [
    "Thanks, but I didn't quite catch that. Here's what I can help with:",
    "That's outside what I can do. Here's what I can:",
  ],
  thanks: [
    (f) => `Thank you${withName(f)}! Anything else I can look at?`,
    (f) => `It's my pleasure${withName(f)}. I'm here if anything else comes up.`,
  ],
  goodbyes: [
    (f) => `Thanks so much for chatting${withName(f)}! Come back any time. 👋`,
    (f) => `Thank you${withName(f)}. I'll be here whenever you need me.`,
  ],
  reportProblemPrompts: [
    "Thanks for telling me, and sorry about that. What went wrong? Include the case number if there is one and I'll pass it to the team.",
    "I appreciate you flagging it. Let me know what happened (and the case number if it's about a case) and I'll get it to the team.",
  ],
  askSummary: [
    'Of course. In a sentence or two, what would you like support to help with?',
    "Thanks. Tell me briefly what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "No problem, I've left that. Here's what else I can do:",
    "Thanks, cancelled. What else can I help with?",
  ],
};

const teammate: Personality = {
  id: 'teammate',
  name: 'Teammate',
  greetings: [
    (name) => `Hey ${name}, Evie here. What do we need to chase up today?`,
    (name) => `Hi ${name}. Evie here. Got a case you want eyes on?`,
    (name) => `Hey ${name}! Evie here. What's on the list?`,
  ],
  menuIntros: [
    "Here's what I can do:",
    'Pick one and I\'ll get on it:',
  ],
  caseListHeadings: [
    (f, n) => `Right${withName(f)}, here are your ${n} latest cases.`,
    (f, n) => `Your ${n} most recent cases${withName(f)}:`,
  ],
  caseListHints: [
    'Tap one and I\'ll give you the rundown.',
    'Pick a case, or type a number, and I\'ll pull it up.',
  ],
  singleCaseIntros: [
    (f) => `Just one open case${withName(f)}. Here's the status.`,
    (f) => `One case on the go${withName(f)}. Here's where it's at.`,
  ],
  whichCase: [
    'Sure. Which case? Type the number.',
    "No worries. What's the case number?",
  ],
  whichCaseIsSlow: [
    "I'll chase it. Which case?",
    "Let's find out. Which case are you waiting on?",
  ],
  nothingForApproval: [
    'Nothing waiting on you. All clear.',
    "Nothing needs your approval right now. 👍",
  ],
  nothingInProgress: [
    "Nothing in progress right now. Once a case is in, I'll have it here.",
    'Nothing in the works at the moment.',
  ],
  forApprovalIntros: [
    'These need your sign-off:',
    'Waiting on you to approve:',
  ],
  inProgressIntros: [
    "Here's what's being worked on:",
    'In progress right now:',
  ],
  noCases: [
    "No cases on file for your practice yet. Submit one and I'll keep track of it.",
    "Nothing on file yet. Once the first case is in, ask me again and I'll keep you posted.",
  ],
  casesError: [
    "Can't reach your case list right now. Give it a minute and try again?",
    "Your case list isn't loading just now. Try again shortly.",
  ],
  caseNotFound: [
    (r) => `Can't find **${r}** on your account. Check the number, or pick one below.`,
    (r) => `Case **${r}** isn't on your account. Have another look, or choose one below.`,
  ],
  fallbacks: [
    "Didn't catch that one. Try one of these, or type a case number:",
    "Not something I can do, but here's what I can:",
  ],
  thanks: [
    (f) => `No worries${withName(f)}. Anything else?`,
    (f) => `Any time${withName(f)}. Shout if you need another case checked.`,
  ],
  goodbyes: [
    (f) => `Cheers${withName(f)}! Back whenever you need me. 👋`,
    (f) => `See you${withName(f)}. Open the chat any time.`,
  ],
  reportProblemPrompts: [
    "Sorry about that. Tell me what went wrong, with the case number if there is one, and I'll get it to the team.",
    "Ah, not good. What happened? Include the case number if it's about a case and I'll pass it on.",
  ],
  askSummary: [
    'Sure. In a sentence or two, what should support help with?',
    "No worries. Tell me briefly what you need and I'll pass it on.",
  ],
  contactCancelled: [
    "No worries, I've left that. Here's what else I can do:",
    "Dropped. What else?",
  ],
};

export const PERSONALITIES: readonly Personality[] = [
  classic, warm, upbeat, calm, brisk, professional, cheerful, easygoing, coach, concierge, plain,
  curious, reassuring, playful, thoughtful, cando, gentle, crisp, host, collaborative, appreciative, teammate,
];

/** The voice used when a session has no stored personality (sessions saved before voices existed). */
export const DEFAULT_PERSONALITY: Personality = classic;

export const personalityById = (id?: string): Personality => PERSONALITIES.find((p) => p.id === id) ?? DEFAULT_PERSONALITY;

/** Draws a voice for a new session. `pick` is injectable so tests can force one. */
export const pickPersonality = (pick: () => number = Math.random): Personality => pickOne(PERSONALITIES, pick);
